import * as React from 'react';
import { MarkdownProps } from '@common/ts/types';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

/**
 * TELV1-296 follow-up — XSS hardening on the shared Markdown component.
 *
 * Architect-review CRITICAL finding: this component is reused for ALL
 * LLM output that flows through the chat bubble and artifact panel.
 * A prompt-injected respondent transcript could try to emit
 * `<img src=x onerror=...>` or `[click](javascript:...)` inside an
 * assistant reply. React-markdown 9's defaults DO strip those, but the
 * safety was implicit — one prop change (e.g. adding `rehype-raw` to
 * the previous prop-spread) would have silently opened the XSS surface.
 *
 * Making the safety EXPLICIT (load-bearing in code, not in unstated
 * defaults) hardens against future maintainers accidentally bypassing
 * it. The two new defences are:
 *
 *   1. `urlTransform={defaultUrlTransform}` — explicit allowlist of
 *      safe URL schemes (http, https, mailto, tel). `javascript:`,
 *      `data:` and unknown schemes are rejected to a no-op `#`.
 *
 *   2. `rehypePlugins={[[rehypeSanitize, defaultSchema]]}` —
 *      explicit DOM sanitisation with hast-util-sanitize's default
 *      schema. Strips `<script>`, `<iframe>`, `<object>`, dangerous
 *      attributes (`onerror`, `onclick`, etc.).
 *
 * Note also: we no longer spread the remaining props into
 * ReactMarkdown. The old shape let callers pass arbitrary
 * react-markdown options — which is exactly what introduces XSS if
 * someone passes rehypePlugins: [rehypeRaw] or similar. Only the
 * props ReactMarkdown legitimately needs from MarkdownProps are
 * forwarded explicitly.
 */
const sanitizeSchema = defaultSchema;

function MarkdownParagraph({
  children,
  ...props
}: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) {
  return (
    <p className="mb-5 last:mb-0 leading-relaxed" {...props}>
      {children}
    </p>
  );
}

function MarkdownLi({
  children,
  ...props
}: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) {
  return (
    <li className="mb-3 last:mb-0 leading-relaxed" {...props}>
      {children}
    </li>
  );
}

function MarkdownUl({
  children,
  ...props
}: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) {
  return (
    <ul className="list-disc pl-5 mb-5 leading-relaxed space-y-1" {...props}>
      {children}
    </ul>
  );
}

function MarkdownOl({
  children,
  ...props
}: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) {
  return (
    <ol className="list-decimal pl-5 mb-5 leading-relaxed space-y-1" {...props}>
      {children}
    </ol>
  );
}

function MarkdownH1({
  children,
  ...props
}: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) {
  return (
    <h1 className="text-base font-bold mb-3 mt-5 first:mt-0" {...props}>
      {children}
    </h1>
  );
}

function MarkdownH2({
  children,
  ...props
}: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) {
  return (
    <h2 className="text-sm font-semibold mb-2 mt-4 first:mt-0" {...props}>
      {children}
    </h2>
  );
}

function MarkdownH3({
  children,
  ...props
}: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) {
  return (
    <h3 className="text-sm font-medium mb-2 mt-3 first:mt-0" {...props}>
      {children}
    </h3>
  );
}

function MarkdownStrong({
  children,
  ...props
}: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) {
  return (
    <strong className="font-bold" {...props}>
      {children}
    </strong>
  );
}

function Markdown({ isParagraphSpanEl, children }: MarkdownProps) {
  if (!children) return null;

  // Remove ```markdown blocks that break styling
  const cleanedContent = children.replace(/```markdown\n?/g, '').replace(/\n?```$/g, '');
  return (
    <ReactMarkdown
      components={{
        p: isParagraphSpanEl ? 'span' : MarkdownParagraph,
        ul: isParagraphSpanEl ? 'span' : MarkdownUl,
        ol: isParagraphSpanEl ? 'span' : MarkdownOl,
        li: isParagraphSpanEl ? 'span' : MarkdownLi,
        h1: MarkdownH1,
        h2: MarkdownH2,
        h3: MarkdownH3,
        strong: MarkdownStrong,
      }}
      // TELV1-296 follow-up XSS hardening. See module docblock at the
      // top of this file for the rationale. Both are explicit defences
      // (load-bearing in code) — the prior implicit defaults could be
      // disabled by a single prop-spread regression.
      urlTransform={defaultUrlTransform}
      rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
    >
      {cleanedContent}
    </ReactMarkdown>
  );
}

export default Markdown;
